import axios from "axios";

const API_URL = process.env.REACT_APP_API_URL || "http://127.0.0.1:3000/api";

const client = axios.create({
  baseURL: API_URL,
  headers: {
    "Content-Type": "application/json",
  },
});

// Auth event listeners for token expiration handling
const authEventListeners: (() => void)[] = [];

export const addAuthEventListener = (listener: () => void) => {
  authEventListeners.push(listener);
};

export const removeAuthEventListener = (listener: () => void) => {
  const index = authEventListeners.indexOf(listener);
  if (index > -1) {
    authEventListeners.splice(index, 1);
  }
};

const notifyAuthError = () => {
  authEventListeners.forEach((listener) => listener());
};

// Add auth token to requests if it exists
client.interceptors.request.use((config) => {
  const token = localStorage.getItem("token");
  if (token) {
    config.headers.Authorization = `Bearer ${token}`;
  }
  return config;
});

// Handle authentication errors
client.interceptors.response.use(
  (response) => response,
  (error) => {
    // If we get a 401, the token is expired or invalid. A 403 means the
    // token is fine but access is denied — don't destroy the session for that.
    if (error.response?.status === 401) {
      // Clear the invalid token
      localStorage.removeItem("token");

      // Notify auth event listeners
      notifyAuthError();

      // Only redirect if we're not already on the login page
      if (!window.location.pathname.startsWith("/login")) {
        // Redirect to login page
        window.location.href = "/login";
      }
    }

    return Promise.reject(error);
  }
);

// API endpoints
export const api = {
  auth: {
    requestLogin: (email: string) =>
      client.post("/auth/request-login", { email }),
    verifyToken: (token: string) => client.post("/auth/verify", { token }),
  },
  settings: {
    getProfile: () => client.get("/settings/profile"),
  },
  analytics: {
    getDashboard: (sdk: string) =>
      client.get("/analytics/dashboard", {
        params: { sdk },
      }),
  },
  crossChain: {
    getFees: () => client.get<CrossChainFees>("/cross-chain/fees"),
    updateFees: (data: UpdateCrossChainFees) =>
      client.post("/cross-chain/fees", data),
    getStats: (startDate: string, endDate: string) =>
      client.get<CrossChainStats>("/cross-chain/stats", {
        params: { startDate, endDate },
      }),
    getOrders: (offset: number) =>
      client.get<CrossChainOrders>("/cross-chain/orders", {
        params: { offset },
      }),
    getClaims: (offset: number) =>
      client.get<CrossChainClaims>("/cross-chain/claims", {
        params: { offset },
      }),
    claim: (balance: ClaimBalance) =>
      client.post<{ claim: CrossChainClaim }>("/cross-chain/claims", {
        settlementChain: balance.settlementChain,
        settlementAsset: balance.settlementAsset,
        payoutChain: balance.payoutChain,
        payoutAsset: balance.payoutAsset,
        payoutAddress: balance.payoutAddress,
      }),
  },
};

// The message from a {status:"error", message} response, or the fallback.
export const apiErrorMessage = (error: any, fallback: string): string =>
  error?.response?.data?.message || fallback;

// Cross-chain amounts are integer strings in the asset's smallest unit (6
// decimals for both Solana USDC and Spark USDB); USD values are decimal strings.

export type CrossChainFees = {
  enabled: boolean;
  feePercentage: number;
  payoutChain: string;
  payoutAsset: string;
  payoutAddress: string;
  // Identifies the settings as loaded; sent back with a save so one based on
  // stale settings (e.g. from another tab) is refused
  version: string;
};

export type UpdateCrossChainFees = {
  feePercentage: number;
  payoutChain: string;
  payoutAsset: string;
  payoutAddress: string;
  version: string;
};

export type ClaimBalance = {
  settlementChain: string;
  settlementAsset: string;
  payoutChain: string;
  payoutAsset: string;
  payoutAddress: string;
  availableBalance: string;
  // False for fees earned under an earlier payout address
  isCurrentPayoutDestination: boolean;
  claimable: boolean;
  unclaimableCode?: string;
  unclaimableReason?: string;
};

export type CrossChainMetrics = {
  volumeUsd: string;
  earnedAmount: string;
  orderCount: number;
  completedCount: number;
  granularity: string;
  series: {
    date: string;
    volumeUsd: string;
    earnedAmount: string;
    orderCount: number;
  }[];
};

// All-time totals, in the settlement assets' smallest units
export type CrossChainTotals = {
  totalEarnedAmount: string;
  availableBalance: string;
  pendingClaimAmount: string;
  paidOutAmount: string;
};

// available is false when Orchestra has no data to show (e.g. while the fee is
// turned off), which must not be shown as zero
export type CrossChainStats = {
  available: boolean;
  metrics: CrossChainMetrics | null;
  totals: CrossChainTotals | null;
  balances: ClaimBalance[];
};

export type CrossChainOrder = {
  operationId: string;
  feeBps: number;
  settlementChain: string;
  settlementAsset: string;
  amount: string | null;
  txHash: string | null;
  paidAt: string | null;
  createdAt: string;
  sourceChain: string | null;
  sourceAsset: string | null;
  destinationChain: string | null;
  destinationAsset: string | null;
  operationStatus: string | null;
};

export type CrossChainOrders = {
  available: boolean;
  orders: CrossChainOrder[];
  nextOffset: number | null;
};

export type CrossChainClaim = {
  id: string;
  amount: string;
  settlementChain: string;
  settlementAsset: string;
  payoutChain: string;
  payoutAsset: string;
  payoutAddress: string;
  status: "processing" | "completed" | "failed";
  txHash: string | null;
  errorMessage: string | null;
  createdAt: string;
  completedAt: string | null;
};

export type CrossChainClaims = {
  available: boolean;
  claims: CrossChainClaim[];
  nextOffset: number | null;
};

export type ApiError = {
  status: "error";
  message: string;
};

export type ApiResponse<T> = {
  status: "success";
  data: T;
};

export default client;
